import type { GeoJSONSource, Map as MapboxMap } from 'mapbox-gl';
import type { Coordinate } from '@shared/models/geography.model';
import type { Building } from '@shared/models/design/building-design.model';
import type { DraftShape, Storey } from '@shared/models/design/storey.model';
import type { Point2D } from '@shared/models/geometry.model';
import { moveCoordinate } from '@shared/helpers/geometry/coordinates.helpers';
import type { FeatureCollection, Polygon } from 'geojson';
import { moveLayerBeforeIfNeeded } from '../helpers/map-layer-order.helpers';
import {
  MapLayerIds,
  otherBuildingsLayerOpacity,
} from '../helpers/map-config.constants';

type BasemapBuildingOpacityProperty =
  'model-opacity' | 'fill-extrusion-opacity';

const BUILDING_LAYER_OPACITY_PROPERTY: Record<
  'model' | 'fill-extrusion',
  BasemapBuildingOpacityProperty
> = {
  model: 'model-opacity',
  'fill-extrusion': 'fill-extrusion-opacity',
};

type MergedStyleLayer = {
  id: string;
  type: string;
  /** Import id (e.g. `basemap`); empty on parent-style layers. */
  scope?: string;
};

type LayerPaintApi = {
  getPaintProperty: (
    layerId: string,
    name: BasemapBuildingOpacityProperty,
  ) => unknown;
  setPaintProperty: (
    layerId: string,
    name: BasemapBuildingOpacityProperty,
    value: number,
  ) => unknown;
  getLayoutProperty?: (layerId: string, name: 'visibility') => unknown;
  setLayoutProperty?: (
    layerId: string,
    name: 'visibility',
    value: 'visible' | 'none',
  ) => unknown;
};

type MapStyleWithMergedLayers = {
  _mergedLayers: Record<string, MergedStyleLayer>;
  getFragmentStyle: (fragmentId?: string) => LayerPaintApi | undefined;
};

type LayerOpacityBackup = {
  layerId: string;
  scope: string;
  property: BasemapBuildingOpacityProperty;
  value: number;
};

const basemapOpacityBackupByMap = new WeakMap<
  MapboxMap,
  Map<string, LayerOpacityBackup>
>();

type LayerVisibilityBackup = {
  layerId: string;
  scope: string;
  value: 'visible' | 'none';
};

const basemapVisibilityBackupByMap = new WeakMap<
  MapboxMap,
  Map<string, LayerVisibilityBackup>
>();

const CLIP_LAYER_TYPES: ['model'] = ['model'];
const CLIP_LAYER_SCOPE: ['basemap'] = ['basemap'];

export const BUILDING_MASK_RADIUS_METERS = 20;
export const BUILDING_CLIP_SOURCE_ID = 'nodon-building-clip-source';
export const BUILDING_CLIP_LAYER_ID = 'nodon-building-clip-layer';

const EMPTY_CLIP_FEATURE_COLLECTION: FeatureCollection<Polygon> = {
  type: 'FeatureCollection',
  features: [],
};

/** Full-world clip used when the background map is hidden (no Mapbox 3D models). */
export const WORLD_BUILDING_CLIP_POLYGON: Polygon = {
  type: 'Polygon',
  coordinates: [
    [
      [-180, -85],
      [180, -85],
      [180, 85],
      [-180, 85],
      [-180, -85],
    ],
  ],
};

type Bounds = {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
};

export type EnsureBuildingClipLayerResult = {
  ready: boolean;
  created: boolean;
};

/**
 * Add the clip source/layer if missing. Moves the clip layer to the top only
 * when it is not already there.
 */
export const ensureBuildingClipLayer = (
  map: MapboxMap,
): EnsureBuildingClipLayerResult => {
  try {
    let created = false;
    if (!map.getSource(BUILDING_CLIP_SOURCE_ID)) {
      map.addSource(BUILDING_CLIP_SOURCE_ID, {
        type: 'geojson',
        data: EMPTY_CLIP_FEATURE_COLLECTION,
      });
      created = true;
    }
    if (!map.getLayer(BUILDING_CLIP_LAYER_ID)) {
      map.addLayer({
        id: BUILDING_CLIP_LAYER_ID,
        type: 'clip',
        source: BUILDING_CLIP_SOURCE_ID,
        layout: {
          'clip-layer-types': CLIP_LAYER_TYPES,
          'clip-layer-scope': CLIP_LAYER_SCOPE,
        },
      });
      created = true;
    } else {
      moveLayerBeforeIfNeeded(map, BUILDING_CLIP_LAYER_ID, undefined);
    }
    return { ready: true, created };
  } catch (err) {
    console.warn('Failed to ensure building clip layer', err);
    return { ready: false, created: false };
  }
};

/**
 * Replace the clip source with {@link polygons} (one hole per building, or
 * the world clip). An empty list clears the clip.
 */
export const updateBuildingClipSource = (
  map: MapboxMap,
  polygons: readonly Polygon[] | undefined,
): void => {
  const source = map.getSource(BUILDING_CLIP_SOURCE_ID) as
    GeoJSONSource | undefined;
  if (!source) return;
  source.setData(getClipFeatureCollection(polygons));
};

/**
 * Clip holes that hide Mapbox 3D buildings around each project footprint.
 * One padded rectangle per building so the street between volumes stays
 * visible. Draft vertices grow the selected building's hole while drawing.
 */
export const computeBuildingMaskPolygons = (
  storeys: Storey[],
  draftShape: DraftShape | undefined,
  location: Coordinate,
  otherBuildings: readonly Building[] = [],
): Polygon[] => {
  const polygons: Polygon[] = [];
  const selectedPolygon = polygonFromBounds(
    getShapeBounds(storeys, draftShape),
    location,
  );
  if (selectedPolygon) polygons.push(selectedPolygon);
  for (const building of otherBuildings) {
    const siblingPolygon = polygonFromBounds(
      getShapeBounds(building.storeys, undefined),
      location,
    );
    if (siblingPolygon) polygons.push(siblingPolygon);
  }
  return polygons;
};

/**
 * Expand footprint bounds by {@link BUILDING_MASK_RADIUS_METERS} and convert
 * to a geographic clip polygon around {@link location}.
 */
const polygonFromBounds = (
  bounds: Bounds | undefined,
  location: Coordinate,
): Polygon | undefined => {
  if (!bounds) return undefined;
  const expanded = {
    minX: bounds.minX - BUILDING_MASK_RADIUS_METERS,
    maxX: bounds.maxX + BUILDING_MASK_RADIUS_METERS,
    minY: bounds.minY - BUILDING_MASK_RADIUS_METERS,
    maxY: bounds.maxY + BUILDING_MASK_RADIUS_METERS,
  };
  const ring = boundsToCoordinateRing(expanded, location);
  if (!ring.length) return undefined;
  return {
    type: 'Polygon',
    coordinates: [ring],
  };
};

const getShapeBounds = (
  storeys: Storey[],
  draftShape: DraftShape | undefined,
): Bounds | undefined => {
  let bounds: Bounds | undefined;
  const registerPoint = (point?: Point2D): void => {
    if (!point) return;
    const [x, y] = point;
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    // TODO use toRect
    bounds = bounds
      ? {
          minX: Math.min(bounds.minX, x),
          maxX: Math.max(bounds.maxX, x),
          minY: Math.min(bounds.minY, y),
          maxY: Math.max(bounds.maxY, y),
        }
      : {
          minX: x,
          maxX: x,
          minY: y,
          maxY: y,
        };
  };
  const registerShape = (shape?: { points?: Point2D[] }) => {
    if (!shape?.points?.length) return;
    shape.points.forEach(registerPoint);
  };
  storeys.forEach((storey) => storey.shapes?.forEach(registerShape));
  registerShape(draftShape);
  return bounds;
};

const boundsToCoordinateRing = (
  bounds: Bounds | undefined,
  origin: Coordinate,
): number[][] => {
  if (!bounds) return [];
  const corners: Point2D[] = [
    [bounds.minX, bounds.minY],
    [bounds.maxX, bounds.minY],
    [bounds.maxX, bounds.maxY],
    [bounds.minX, bounds.maxY],
  ];
  const coordinates = corners.map((point) => {
    const coordinate = translateByMeters(origin, point);
    return [coordinate.lng, coordinate.lat];
  });
  const firstCoordinate = coordinates[0];
  if (firstCoordinate) {
    coordinates.push([...firstCoordinate]);
  }
  return coordinates;
};

const translateByMeters = (origin: Coordinate, [x, y]: Point2D): Coordinate => {
  const eastWest = moveCoordinate(
    origin,
    [Math.abs(x), 'meters'],
    [x >= 0 ? 90 : 270, 'degrees'],
  );
  return moveCoordinate(
    eastWest,
    [Math.abs(y), 'meters'],
    [y >= 0 ? 180 : 0, 'degrees'],
  );
};

/**
 * Fade Mapbox Standard 3D buildings (imported model / fill-extrusion layers).
 * Restores previous paint when {@link opacity} is 1.
 *
 * Imported Standard layers are not addressable via {@link MapboxMap.setPaintProperty}
 * using `_mergedLayers` keys — those are FQIDs (`layerId` + unit separator + import
 * id). Paint is applied on the import fragment with the unscoped layer id.
 */
export const applyBasemapBuildingsOpacity = (
  map: MapboxMap,
  opacity: number,
): void => {
  const layers = getMergedStyleLayers(map);
  const backup = getOrCreateOpacityBackup(map);

  if (opacity >= 1) {
    restoreBasemapBuildingsOpacity(map, backup);
    return;
  }

  let didChange = false;
  for (const layer of Object.values(layers)) {
    if (!isPaintableBasemapLayer(layer)) continue;
    const property = getBuildingLayerOpacityProperty(layer.type);
    if (!property) continue;
    const scope = layer.scope ?? '';
    const paintApi = getLayerPaintApi(map, scope);
    if (!paintApi) continue;
    const backupKey = getOpacityBackupKey(layer);
    if (!backup.has(backupKey)) {
      const current = paintApi.getPaintProperty(layer.id, property);
      backup.set(backupKey, {
        layerId: layer.id,
        scope,
        property,
        value: typeof current === 'number' ? current : 1,
      });
    }
    paintApi.setPaintProperty(layer.id, property, opacity);
    didChange = true;
  }
  if (didChange) requestMapStyleRepaint(map);
};

/**
 * Hide or restore imported Standard layers. Covering them with a slotted
 * fill is not enough: zoom re-merges the import and the map flashes through.
 */
export const applyImportedBasemapVisibility = (
  map: MapboxMap,
  visible: boolean,
): void => {
  const backup = getOrCreateVisibilityBackup(map);
  if (visible) {
    restoreImportedBasemapVisibility(map, backup);
    return;
  }

  const layers = getMergedStyleLayers(map);
  let didChange = false;
  for (const layer of Object.values(layers)) {
    if (!isPaintableBasemapLayer(layer)) continue;
    if (!layer.scope) continue;
    const fragment = getLayerPaintApi(map, layer.scope);
    if (!fragment?.getLayoutProperty || !fragment.setLayoutProperty) continue;
    const backupKey = getOpacityBackupKey(layer);
    if (!backup.has(backupKey)) {
      const current = fragment.getLayoutProperty(layer.id, 'visibility');
      backup.set(backupKey, {
        layerId: layer.id,
        scope: layer.scope,
        value: current === 'none' ? 'none' : 'visible',
      });
    }
    const current = fragment.getLayoutProperty(layer.id, 'visibility');
    if (current === 'none') continue;
    fragment.setLayoutProperty(layer.id, 'visibility', 'none');
    didChange = true;
  }
  if (didChange) requestMapStyleRepaint(map);
};

/**
 * Scale the static sibling-building GeoJSON fill/line used for hit-testing.
 */
export const applyOtherBuildingsLayerOpacity = (
  map: MapboxMap,
  opacity: number,
): void => {
  if (map.getLayer(MapLayerIds.OtherBuildingsFill)) {
    map.setPaintProperty(
      MapLayerIds.OtherBuildingsFill,
      'fill-opacity',
      otherBuildingsLayerOpacity(opacity),
    );
  }
  if (map.getLayer(MapLayerIds.OtherBuildingsLine)) {
    map.setPaintProperty(
      MapLayerIds.OtherBuildingsLine,
      'line-opacity',
      otherBuildingsLayerOpacity(opacity),
    );
  }
};

/**
 * Paint property that controls opacity for a Mapbox building layer type.
 */
export const getBuildingLayerOpacityProperty = (
  layerType: string,
): BasemapBuildingOpacityProperty | undefined => {
  if (layerType === 'model' || layerType === 'fill-extrusion') {
    return BUILDING_LAYER_OPACITY_PROPERTY[layerType];
  }
  return undefined;
};

const restoreBasemapBuildingsOpacity = (
  map: MapboxMap,
  backup: Map<string, LayerOpacityBackup>,
): void => {
  let didChange = false;
  for (const { layerId, scope, property, value } of backup.values()) {
    const paintApi = getLayerPaintApi(map, scope);
    if (!paintApi) continue;
    paintApi.setPaintProperty(layerId, property, value);
    didChange = true;
  }
  backup.clear();
  basemapOpacityBackupByMap.delete(map);
  if (didChange) requestMapStyleRepaint(map);
};

const getOrCreateOpacityBackup = (
  map: MapboxMap,
): Map<string, LayerOpacityBackup> => {
  const existing = basemapOpacityBackupByMap.get(map);
  if (existing) return existing;
  const created = new Map<string, LayerOpacityBackup>();
  basemapOpacityBackupByMap.set(map, created);
  return created;
};

const getOrCreateVisibilityBackup = (
  map: MapboxMap,
): Map<string, LayerVisibilityBackup> => {
  const existing = basemapVisibilityBackupByMap.get(map);
  if (existing) return existing;
  const created = new Map<string, LayerVisibilityBackup>();
  basemapVisibilityBackupByMap.set(map, created);
  return created;
};

const restoreImportedBasemapVisibility = (
  map: MapboxMap,
  backup: Map<string, LayerVisibilityBackup>,
): void => {
  if (backup.size === 0) return;
  let didChange = false;
  for (const { layerId, scope, value } of backup.values()) {
    const fragment = getLayerPaintApi(map, scope);
    if (!fragment?.setLayoutProperty) continue;
    const current = fragment.getLayoutProperty?.(layerId, 'visibility');
    const normalized = current === 'none' ? 'none' : 'visible';
    if (normalized === value) continue;
    fragment.setLayoutProperty(layerId, 'visibility', value);
    didChange = true;
  }
  backup.clear();
  basemapVisibilityBackupByMap.delete(map);
  if (didChange) requestMapStyleRepaint(map);
};

const MAPBOX_FQID_SEPARATOR = '\u001f';

const isNodonLayerId = (layerId: string): boolean =>
  layerId.startsWith('nodon-') || layerId.startsWith('map-');

/** Skip FQID keys accidentally treated as layer ids (`building-models` + US + `basemap`). */
const isPaintableBasemapLayer = (layer: MergedStyleLayer): boolean => {
  if (!layer.id) return false;
  if (layer.id.includes(MAPBOX_FQID_SEPARATOR)) return false;
  if (isNodonLayerId(layer.id)) return false;
  return true;
};

const hasMergedLayers = (value: unknown): value is MapStyleWithMergedLayers => {
  if (!value || typeof value !== 'object') return false;
  if (!('_mergedLayers' in value) || !('getFragmentStyle' in value)) {
    return false;
  }
  return (
    typeof value._mergedLayers === 'object' &&
    value._mergedLayers !== null &&
    typeof value.getFragmentStyle === 'function'
  );
};

const getMapStyle = (map: MapboxMap): MapStyleWithMergedLayers | undefined => {
  if (!('style' in map)) return undefined;
  const style = Reflect.get(map, 'style');
  if (!hasMergedLayers(style)) return undefined;
  return style;
};

/**
 * Mapbox Standard buildings live in an imported style; parent `getStyle().layers`
 * does not list them. `_mergedLayers` is the flattened layer set Mapbox uses to paint.
 */
const getMergedStyleLayers = (
  map: MapboxMap,
): Record<string, MergedStyleLayer> => {
  const style = getMapStyle(map);
  if (!style) return {};
  return style._mergedLayers;
};

/**
 * Parent-style layers use {@link MapboxMap} paint APIs; imported layers live on
 * the fragment named by {@link MergedStyleLayer.scope}.
 */
const getLayerPaintApi = (
  map: MapboxMap,
  scope: string,
): LayerPaintApi | undefined => {
  if (!scope) return map;
  return getMapStyle(map)?.getFragmentStyle(scope);
};

const getOpacityBackupKey = (layer: MergedStyleLayer): string =>
  layer.scope ? `${layer.id}:${layer.scope}` : layer.id;

/**
 * Fragment paint updates share Mapbox's style changeset but skip {@link MapboxMap}
 * `_update(true)`. Mark the style dirty so the next frame applies opacity.
 */
const requestMapStyleRepaint = (map: MapboxMap): void => {
  const update = Reflect.get(map, '_update');
  if (typeof update === 'function') {
    update.call(map, true);
    return;
  }
  map.triggerRepaint();
};

const getClipFeatureCollection = (
  polygons: readonly Polygon[] | undefined,
): FeatureCollection<Polygon> => {
  if (!polygons?.length) {
    return EMPTY_CLIP_FEATURE_COLLECTION;
  }
  return {
    type: 'FeatureCollection',
    features: polygons.map((polygon) => ({
      type: 'Feature',
      properties: {},
      geometry: polygon,
    })),
  };
};
